import { maskToPolygon, type Point } from "./mask-to-polygon";

type ProgressCallback = (progress: {
  status: string;
  file?: string;
  progress?: number;
}) => void;

const MODEL_ID = "Xenova/slimsam-77-uniform";

let worker: Worker | null = null;
let modelLoaded = false;
let imageEncoded = false;

type PendingResolve<T> = { resolve: (v: T) => void; reject: (e: Error) => void };
let loadResolver: PendingResolve<void> | null = null;
let encodeResolver: PendingResolve<ImageEmbedding> | null = null;
let segmentResolver: PendingResolve<SegmentResult> | null = null;
let progressCallback: ProgressCallback | undefined;

function getWorker(): Worker {
  if (!worker) {
    worker = new Worker(new URL("./sam-worker.ts", import.meta.url), { type: "module" });
    worker.onmessage = handleWorkerMessage;
    worker.onerror = (e) => {
      const err = new Error(e.message ?? "Worker error");
      loadResolver?.reject(err);
      encodeResolver?.reject(err);
      segmentResolver?.reject(err);
    };
  }
  return worker;
}

function handleWorkerMessage(e: MessageEvent) {
  const msg = e.data;
  switch (msg.type) {
    case "progress":
      progressCallback?.({
        status: "downloading",
        file: msg.file,
        progress: msg.progress,
      });
      break;
    case "loaded":
      modelLoaded = true;
      loadResolver?.resolve();
      loadResolver = null;
      break;
    case "encoded":
      imageEncoded = true;
      encodeResolver?.resolve({
        originalWidth: msg.width,
        originalHeight: msg.height,
      });
      encodeResolver = null;
      break;
    case "segmented": {
      const maskData = msg.maskData as number[];
      const { width, height, score } = msg;
      const binaryMask = new Float32Array(maskData.length);
      for (let i = 0; i < maskData.length; i++) {
        if (maskData[i] > 0) binaryMask[i] = 1;
      }
      const polygon = maskToPolygon(binaryMask, width, height);
      console.log("[SAM] segment result:", polygon.length, "points, score:", score);
      segmentResolver?.resolve({ polygon, score });
      segmentResolver = null;
      break;
    }
    case "error": {
      const err2 = new Error(msg.error);
      loadResolver?.reject(err2);
      encodeResolver?.reject(err2);
      segmentResolver?.reject(err2);
      loadResolver = null;
      encodeResolver = null;
      segmentResolver = null;
      break;
    }
  }
}

export type ImageEmbedding = {
  originalWidth: number;
  originalHeight: number;
};

export type SegmentResult = {
  polygon: Point[];
  score: number;
};

export async function loadSamModel(onProgress?: ProgressCallback): Promise<void> {
  if (modelLoaded) return;
  progressCallback = onProgress;
  const w = getWorker();
  return new Promise<void>((resolve, reject) => {
    loadResolver = { resolve, reject };
    w.postMessage({ type: "load", modelId: MODEL_ID });
  });
}

export async function encodeImage(
  imageUrl: string,
  _onProgress?: ProgressCallback
): Promise<ImageEmbedding> {
  if (!modelLoaded) throw new Error("Model not loaded");
  const w = getWorker();
  return new Promise<ImageEmbedding>((resolve, reject) => {
    encodeResolver = { resolve, reject };
    w.postMessage({ type: "encode", imageUrl });
  });
}

export async function segmentClick(
  _embedding: ImageEmbedding,
  x: number,
  y: number
): Promise<SegmentResult> {
  if (!imageEncoded) throw new Error("Image not encoded");
  const w = getWorker();
  return new Promise<SegmentResult>((resolve, reject) => {
    segmentResolver = { resolve, reject };
    w.postMessage({ type: "segment", x, y });
  });
}

export function isModelLoaded(): boolean {
  return modelLoaded;
}
